import { useContext, useEffect, useState, type Dispatch } from "react";
import { useTranslation } from "react-i18next";
import type { Review } from "../../../api/content/reviewDecoders";
import { decideOnReview, mergeReview, resubmitChangeSet } from "../../../api/content/reviewsApi";
import type { SubmissionHandle } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import type { VersionPlan } from "../../../api/content/versionDecoders";
import MergeVersionFields from "../../versions/merge/MergeVersionFields";
import { useVersionPlan } from "../../versions/merge/useVersionPlan";
import { confirmedBumps, NO_CHANGED_BUMPS } from "../../versions/merge/versionConfirmation";
import { actionReady, commentRequired, type DecisionAction, type DecisionInput } from "../state/decisionRules";
import type { ReviewPageEvent, ReviewPageState } from "../state/reviewPageState";
import { decisionComment } from "../state/mergeBlockReport";
import { unnamedItemVersions } from "../checks/checkFindings";
import { ItemNameContext } from "../../items/itemNameContext";
import { ACTION_LABEL_KEYS, changedItemName } from "./decisionLabels";
import OptionLabel from "../../../ui/forms/OptionLabel";

/**
 * The comment for one decision, and for a merge with open findings the acknowledgement and
 * its note, recorded with the merge (R-3). A merge also confirms the version labels DMW
 * proposes, with a change line (ADR-0033 D3). Sends exactly the head shown (R-1).
 */
export default function DecisionForm({
  action,
  review,
  state,
  dispatch,
  submission,
  unmarked,
  prefill,
  onCancel,
  onSent,
}: {
  action: DecisionAction;
  review: Review;
  state: ReviewPageState;
  dispatch: Dispatch<ReviewPageEvent>;
  submission: SubmissionHandle;
  /** For an approval: how many of the caller's units have no OK mark (CU-5). */
  unmarked: number;
  /** For request changes: the caller's "needs change" notes, to start the comment. */
  prefill: string;
  onCancel: () => void;
  onSent: () => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "en";
  const [input, setInput] = useState<DecisionInput>({
    comment: decisionComment(action, review, prefill, t, language),
    note: "",
    changeLine: review.changeSet.title,
    bumps: NO_CHANGED_BUMPS,
  });
  const itemNames = useContext(ItemNameContext);
  // The report names items the review does not list once the item name cache has read them, unless the person has started editing it.
  useEffect(() => {
    const shapes = review.mergeBlocks.shapeViolations;
    const unnamed = unnamedItemVersions(review.items, [...shapes.introduced, ...shapes.preExisting]);
    if (itemNames === null || unnamed.length === 0 || action !== "request_changes") return;
    let current = true;
    void Promise.all(unnamed.map(({ iri, reference }) => itemNames.nameOf(reference).then((name) => [iri, name] as const))).then((found) => {
      if (!current) return;
      const named = Object.fromEntries(found.flatMap(([iri, name]) => (name === null ? [] : [[iri, name] as const])));
      const first = decisionComment(action, review, prefill, t, language);
      setInput((previous) => (previous.comment === first ? { ...previous, comment: decisionComment(action, review, prefill, t, language, named) } : previous));
    });
    return () => {
      current = false;
    };
  }, [action, itemNames, language, prefill, review, t]);
  const plan = useVersionPlan(action === "merge" ? review.changeSet.changeSetId : null, review.head);
  const needsNote = action === "merge" && !review.mergeBlocks.blocked && review.openFindings.needsAcknowledgement;
  const busy = submission.state.status === "submitting";
  // A merge waits for the plan, so the person sees what DMW proposes; without one, the proposal stands unconfirmed.
  const planPending = action === "merge" && plan.state.status === "loading";
  const send = () => {
    if (!actionReady(action, review, state, input)) return;
    const versionPlan = plan.state.status === "loaded" ? plan.state.value : null;
    submission.submit(async () => {
      await sendDecision(action, review, input, versionPlan, { resubmitReason: t("reviews.decisions.resubmitReason") });
      onSent();
    });
  };

  const comment = (
    <textarea
      className={FORM_COMPACT_CONTROL_CLASS}
      rows={2}
      autoFocus={!needsNote}
      required={commentRequired(action)}
      value={input.comment}
      aria-label={t(`reviews.decisions.commentFor.${action}`)}
      placeholder={t(`reviews.decisions.commentFor.${action}`)}
      onChange={(event) => {
        setInput({ ...input, comment: event.target.value });
      }}
    />
  );

  return (
    <form
      className="space-y-2 border-t border-[var(--app-border)] pt-2"
      onSubmit={(event) => {
        event.preventDefault();
        send();
      }}
    >
      {needsNote ? null : comment}
      {needsNote ? (
        <>
          <OptionLabel align="start">
            <input
              type="checkbox"
              checked={state.acknowledged}
              onChange={(event) => {
                dispatch({ type: "acknowledgementChanged", acknowledged: event.target.checked });
              }}
            />
            <span>{t("reviews.merge.acknowledge")}</span>
          </OptionLabel>
          <input
            className={FORM_COMPACT_CONTROL_CLASS}
            autoFocus={true}
            value={input.note}
            aria-label={t("reviews.merge.note")}
            placeholder={t("reviews.merge.note")}
            onChange={(event) => {
              setInput({ ...input, note: event.target.value });
            }}
          />
        </>
      ) : null}
      {needsNote ? comment : null}
      {action === "approve" && unmarked > 0 ? <p className="app-status-text-warning text-sm">{t("reviews.curation.decide.unmarked", { count: unmarked })}</p> : null}
      {action === "merge" ? (
        <MergeVersionFields
          plan={plan}
          nameOf={(itemId) => changedItemName(review, itemId)}
          choices={input.bumps}
          onChoicesChange={(bumps) => {
            setInput({ ...input, bumps });
          }}
          changeLine={input.changeLine}
          onChangeLineChange={(changeLine) => {
            setInput({ ...input, changeLine });
          }}
        />
      ) : null}
      <div className="flex justify-end gap-2">
        <AppButton size="sm" variant="ghost" onClick={onCancel}>
          {t("common.cancel")}
        </AppButton>
        <AppButton type="submit" size="sm" variant={action === "scrap" ? "danger" : "primary"} disabled={busy || planPending || !actionReady(action, review, state, input)}>
          {t(ACTION_LABEL_KEYS[action])}
        </AppButton>
      </div>
    </form>
  );
}

/** The request each action sends, on the head the caller saw. */
function sendDecision(
  action: DecisionAction,
  review: Review,
  input: DecisionInput,
  plan: VersionPlan | null,
  defaults: { readonly resubmitReason: string },
): Promise<unknown> {
  const changeSetId = review.changeSet.changeSetId;
  const comment = input.comment.trim();
  switch (action) {
    case "merge":
      return mergeReview(changeSetId, {
        reviewedHead: review.head,
        // The curator's own reason, required (FR-VERSION-3); never a default that states who approved.
        reason: comment,
        acknowledgement: !review.mergeBlocks.blocked && review.openFindings.needsAcknowledgement
          ? { checkRunId: review.checks?.checkRunId ?? null, conflictCheckId: review.conflictCheck?.conflictCheckId ?? null, note: input.note.trim() }
          : null,
        changeLine: input.changeLine.trim(),
        // Only a plan for the head shown is confirmed; the server refuses a stale head anyway.
        ...(plan !== null && plan.head === review.head ? confirmedBumps(plan, input.bumps) : { versionBump: null, itemBumps: {} }),
      });
    case "resubmit":
      return resubmitChangeSet(changeSetId, comment || defaults.resubmitReason);
    case "approve":
    case "request_changes":
    case "scrap":
      return decideOnReview(changeSetId, { kind: action, reviewedHead: review.head, comment });
  }
}
